import { useState, useEffect, useCallback } from 'react';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Sparkles, Clock, CheckCircle, AlertCircle, Camera, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';

const statusColors = {
  pending: 'bg-amber-100 text-amber-700',
  in_progress: 'bg-blue-100 text-blue-700',
  completed: 'bg-emerald-100 text-emerald-700',
  inspected: 'bg-purple-100 text-purple-700',
  rejected: 'bg-red-100 text-red-700',
};

export default function Housekeeping() {
  const [dashboard, setDashboard] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [roomStatus, setRoomStatus] = useState([]);
  const [filter, setFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('tasks');

  const loadData = useCallback(async () => {
    try {
      const [dashRes, tasksRes, statusRes] = await Promise.all([
        api.get('/housekeeping/dashboard'),
        api.get('/housekeeping/tasks', { params: { status: filter || undefined } }),
        api.get('/housekeeping/room-status'),
      ]);
      setDashboard(dashRes.data);
      setTasks(tasksRes.data);
      setRoomStatus(statusRes.data);
    } catch { toast.error('Failed to load housekeeping data'); }
    finally { setLoading(false); }
  }, [filter]);

  useEffect(() => { loadData(); }, [loadData]);

  const updateStatus = async (taskId, status) => {
    try {
      await api.put(`/housekeeping/tasks/${taskId}/status?status=${status}`);
      toast.success(`Task ${status}`);
      loadData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-sage-700"></div></div>;

  return (
    <div data-testid="housekeeping-page">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-stone-900 font-['Manrope']">Housekeeping</h1>
        <p className="text-stone-500 mt-1">Room cleaning status and task management</p>
      </div>

      {/* Dashboard Stats */}
      {dashboard && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
          {[
            { label: 'Clean', value: dashboard.rooms.clean, color: 'text-emerald-700 bg-emerald-50' },
            { label: 'Dirty', value: dashboard.rooms.dirty, color: 'text-red-700 bg-red-50' },
            { label: 'Unknown', value: dashboard.rooms.unknown, color: 'text-stone-600 bg-stone-50' },
            { label: 'Pending Tasks', value: dashboard.tasks.pending, color: 'text-amber-700 bg-amber-50' },
            { label: 'Completed Today', value: dashboard.tasks.completed_today, color: 'text-purple-700 bg-purple-50' },
          ].map(s => (
            <div key={s.label} className={`${s.color} rounded-md p-3 text-center`}>
              <p className="text-2xl font-bold font-['Manrope']">{s.value}</p>
              <p className="text-xs mt-0.5">{s.label}</p>
            </div>
          ))}
        </div>
      )}

      {/* Tab Toggle */}
      <div className="flex gap-2 mb-4">
        {['tasks', 'rooms'].map(tab => (
          <Button key={tab} variant={activeTab === tab ? 'default' : 'outline'} size="sm" onClick={() => setActiveTab(tab)}
            className={activeTab === tab ? 'bg-sage-700 text-white' : ''}>{tab === 'tasks' ? 'Tasks' : 'Room Status'}</Button>
        ))}
        {activeTab === 'tasks' && (
          <Select value={filter || 'all'} onValueChange={v => setFilter(v === 'all' ? '' : v)}>
            <SelectTrigger className="w-40 border-stone-300 ml-auto"><SelectValue placeholder="All" /></SelectTrigger>
            <SelectContent className="bg-white">
              <SelectItem value="all">All Tasks</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="in_progress">In Progress</SelectItem>
              <SelectItem value="completed">Completed</SelectItem>
              <SelectItem value="inspected">Inspected</SelectItem>
            </SelectContent>
          </Select>
        )}
      </div>

      {activeTab === 'tasks' ? (
        <div className="space-y-3">
          {tasks.length === 0 ? (
            <div className="bg-white border border-stone-200 rounded-md p-12 text-center">
              <Sparkles className="w-12 h-12 mx-auto mb-3 text-stone-300" />
              <p className="text-stone-500">No housekeeping tasks</p>
            </div>
          ) : tasks.map(task => (
            <div key={task.id} className="bg-white border border-stone-200 rounded-md p-4" data-testid={`task-${task.id}`}>
              <div className="flex items-start justify-between">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-medium text-stone-900">Room {task.room?.room_number || '?'}</span>
                    <span className={`text-xs px-2 py-0.5 rounded-full ${statusColors[task.status]}`}>{task.status.replace('_', ' ')}</span>
                    <span className="text-xs px-2 py-0.5 rounded-full bg-stone-100 text-stone-600">{task.task_type.replace(/_/g, ' ')}</span>
                    {task.priority === 'urgent' && <span className="text-xs px-2 py-0.5 rounded-full bg-red-100 text-red-700">Urgent</span>}
                  </div>
                  {task.assigned_to_name && <p className="text-sm text-stone-500">Assigned to: {task.assigned_to_name}</p>}
                  {task.notes && <p className="text-sm text-stone-400 mt-1">{task.notes}</p>}
                  {task.photos?.length > 0 && <p className="text-xs text-emerald-600 mt-1 flex items-center gap-1"><Camera className="w-3 h-3" />{task.photos.length} photo(s)</p>}
                </div>
                <div className="flex gap-1">
                  {task.status === 'pending' && <Button size="sm" variant="outline" onClick={() => updateStatus(task.id, 'in_progress')} className="text-blue-700 border-blue-200 hover:bg-blue-50">Start</Button>}
                  {task.status === 'in_progress' && <Button size="sm" variant="outline" onClick={() => updateStatus(task.id, 'completed')} className="text-emerald-700 border-emerald-200 hover:bg-emerald-50">Complete</Button>}
                  {task.status === 'completed' && (
                    <>
                      <Button size="sm" variant="outline" onClick={() => updateStatus(task.id, 'inspected')} className="text-purple-700 border-purple-200 hover:bg-purple-50">Approve</Button>
                      <Button size="sm" variant="outline" onClick={() => updateStatus(task.id, 'rejected')} className="text-red-700 border-red-200 hover:bg-red-50">Reject</Button>
                    </>
                  )}
                </div>
              </div>
              {/* Checklist preview */}
              {task.checklist?.length > 0 && (
                <div className="mt-3 pt-3 border-t border-stone-100">
                  <p className="text-xs text-stone-400 mb-1">{task.checklist.filter(c => c.done).length}/{task.checklist.length} items completed</p>
                  <div className="w-full bg-stone-100 rounded-full h-1.5">
                    <div className="bg-emerald-500 h-1.5 rounded-full" style={{ width: `${(task.checklist.filter(c => c.done).length / task.checklist.length) * 100}%` }}></div>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
          {roomStatus.map(room => (
            <div key={room.id} className={`border rounded-md p-3 text-center ${
              room.cleaning_status === 'clean' ? 'border-emerald-300 bg-emerald-50' :
              room.cleaning_status === 'dirty' ? 'border-red-300 bg-red-50' :
              'border-stone-200 bg-stone-50'
            }`} data-testid={`room-status-${room.id}`}>
              <p className="font-semibold text-stone-900">{room.room_number}</p>
              <p className="text-xs text-stone-500 capitalize">{room.room_type}</p>
              <span className={`inline-block mt-2 text-xs px-2 py-0.5 rounded-full ${
                room.cleaning_status === 'clean' ? 'bg-emerald-100 text-emerald-700' :
                room.cleaning_status === 'dirty' ? 'bg-red-100 text-red-700' :
                'bg-stone-100 text-stone-600'
              }`}>{room.cleaning_status || 'unknown'}</span>
              {room.latest_task && <p className="text-xs text-stone-400 mt-1">{room.latest_task.status}</p>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
